import {computed, EventEmitter, Injectable, signal} from '@angular/core';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {SnapshotAction} from '@angular/fire/compat/database/interfaces';
import {TranslateService} from '@ngx-translate/core';
import {BaseDaoService, RefModelStoreService} from '@q9elements/ui-kit/common';
import {find, get, isEqual, set} from 'lodash';
import moment from 'moment';
import {forkJoin, merge, share} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {AngularFirestore, DocumentChangeAction} from '@angular/fire/compat/firestore';

import {AssistantLabels, Assistants, AssistantType} from '../models/assistant.interface';
import {HistoryItem} from '../models/history.interface';
import {Message} from '../models/message.interface';
import {isRunInProgress, RunStatus} from '../models/run';
import {AiChatDaoService} from './ai-chat-dao.service';

@Injectable()
export class AiChatService extends BaseDaoService {
  readonly chatId = signal<string>(null, {equal: isEqual});
  readonly status = signal<RunStatus>('initial');
  readonly chatDate = signal<string | Date>(null);

  readonly apiError = signal<string>(null);
  readonly chatError = signal<string>(null);

  readonly history = signal<HistoryItem[]>(null);
  readonly assistants = signal<Assistants>([]);
  readonly selectedAssistant = signal<AssistantType>(null);
  readonly isAssistantEnabled = computed(() => {
    const type = this.selectedAssistant();
    const assistantId = get(this.rmeStore.refModel, `assistants.${type}`, false);

    return !!assistantId;
  });

  private chatRef: string;
  private readonly messages = new Map<string, Message>();
  private readonly updatesEvent = new EventEmitter();
  private readonly resetEvent = new EventEmitter();
  readonly messages$ = merge(this.updatesEvent, this.resetEvent).pipe(
    tap(({type, message}) => this.updateMessagesByActionType(message, type)),
    map(() => Array.from(this.messages.values())),
    share()
  );
  public readonly assistantName = this.translate.instant('AI_CHAT.ASSISTANT_NAME');

  constructor(
    private rmeStore: RefModelStoreService,
    private fb: AngularFireDatabase,
    private translate: TranslateService,
    private aiChatDaoService: AiChatDaoService,
    private firestore: AngularFirestore
  ) {
    super();

    this.loadAssistants();
  }

  private get chatStatusPath() {
    return `${this.chatRef}/status`;
  }

  private get chatErrorPath() {
    return `${this.chatRef}/error`;
  }

  private get chatMessagesPath() {
    return `${this.chatRef}/messages`;
  }

  processMessage(message: string) {
    this.apiError.set(null);
    this.status.set('queued');

    if (this.chatId()) {
      return this.addMessageToChat(message);
    }

    return this.startChat(message);
  }

  regenerateChat() {
    this.apiError.set(null);
    this.status.set('queued');

    this.aiChatDaoService.regenerateChat(this.chatId()).subscribe({
      error: (error: string) => {
        this.status.set('failed');
        this.apiError.set(error);
      }
    });
  }

  cancelRunningChat() {
    const status = this.status();
    const inProgress = isRunInProgress(status);

    if (!inProgress || !this.chatId()) {
      return void 0;
    }

    this.aiChatDaoService.cancelRunningChat(this.chatId()).subscribe();
  }

  getFileContent(fileId: string) {
    return this.aiChatDaoService.getFileContent(fileId, this.chatId());
  }

  getFileMetadata(fileId: string) {
    return this.aiChatDaoService.getFileMetadata(fileId, this.chatId());
  }

  loadChat(chatId: string) {
    this.aiChatDaoService
      .loadChat(chatId)
      .pipe(takeUntil(this.resetEvent))
      .subscribe(this.createObserver());
  }

  loadHistory() {
    this.aiChatDaoService.getHistory().subscribe({
      next: history => this.history.set(history),
      error: error => {
        this.history.set([]);
        this.apiError.set(error);
      }
    });
  }

  resetAssistant(assistant: AssistantType) {
    this.resetEvent.emit({type: 'clear'});
    this.chatDate.set(null);
    this.apiError.set(null);
    this.chatError.set(null);
    this.chatId.set(null);
    this.chatRef = null;
    this.selectedAssistant.set(assistant);
    this.status.set('initial');
  }

  private loadAssistants() {
    const assistants: Assistants = Object.values(AssistantType).map(type => {
      const label = AssistantLabels[type];

      return {label, type};
    });

    this.assistants.set(assistants);
    this.selectedAssistant.set(assistants[0].type);
  }

  private startChat(message: string) {
    const {next, error} = this.createObserver();
    this.aiChatDaoService
      .startChat({
        message,
        assistantType: this.selectedAssistant()
      })
      .pipe(takeUntil(this.resetEvent))
      .subscribe({
        next: payload => {
          next(payload);
        },
        error
      });
  }

  private addMessageToChat(message: string) {
    const {error} = this.createObserver();

    this.aiChatDaoService
      .addMessageToChat(this.chatId(), message)
      .pipe(takeUntil(this.resetEvent))
      .subscribe({error});
  }

  private createObserver() {
    const isNewChat = !this.chatId();

    return {
      next: ({conversationId, conversationRef}) => {
        this.chatId.set(conversationId);
        this.chatRef = conversationRef;

        this.subscribeToErrorUpdates();
        this.subscribeToStatusUpdates();
        this.subscribeToMessageUpdates();

        if (isNewChat) {
          this.addChatToHistoryFromFirestore();
        } else {
          this.updateChatDateFromFirestore();
        }
      },
      error: (error: string) => {
        this.apiError.set(error);
        this.status.set('failed');
      }
    };
  }

  private subscribeToStatusUpdates() {
    const docPath = this.chatStatusPath.replace(/\/status$/, '');
    this.firestore
      .doc(docPath)
      .valueChanges()
      .pipe(takeUntil(this.resetEvent))
      .subscribe((doc: any) => this.status.set(doc?.status ?? null));
  }

private subscribeToErrorUpdates() {
  const docPath = this.chatErrorPath.replace(/\/error$/, '');
  this.firestore
    .doc(docPath)
    .valueChanges()
    .pipe(takeUntil(this.resetEvent))
    .subscribe((doc: any) => this.chatError.set(doc?.error ?? null));
}

  private subscribeToMessageUpdates() {
    const actionMap = {added: 'child_added', modified: 'child_changed'};

    this.firestore
      .collection(this.chatMessagesPath, ref => ref.orderBy('createdAt').orderBy('__name__'))
      .stateChanges(['added', 'modified'])
      .pipe(takeUntil(this.resetEvent))
      .subscribe((actions: DocumentChangeAction<Message>[]) => {
        actions.forEach(action => {
          this.updatesEvent.emit({
            type: actionMap[action.type],
            message: { id: action.payload.doc.id, ...(action.payload.doc.data() as any) }
          });
        })
      });
  }

  private updateMessagesByActionType(
    message: Message,
    type: 'child_added' | 'child_changed' | 'clear'
  ) {
    if (type === 'child_added') {
      this.messages.set(message.id, message);
    } else if (type === 'child_changed') {
      this.updateContentById(message.id, message);
    } else if (type === 'clear') {
      this.messages.clear();
    }
  }

  private updateContentById(id: string, message: Message) {
    const existingMessage = this.messages.get(id);

    if (!existingMessage) {
      return void 0;
    }

    message.content.forEach((item, index) => {
      const existingContent = existingMessage.content[index];

      if (!isEqual(existingContent, item)) {
        set(existingMessage, `content[${index}].value`, item.value);
      }
    });
  }

  private updateChatDate() {
    this.fb.database.ref(`${this.chatRef}/createdAt`).once('value', snapshot => {
      const date = moment(snapshot.val()).format('DD MMMM YYYY, HH:mm');

      this.chatDate.set(date);
    });
  }

  private addChatToHistory() {
    this.fb.database.ref(this.chatRef).once('value', snapshot => {
      const chat = snapshot.val();
      const date = moment(chat.createdAt).format('DD MMMM YYYY, HH:mm');
      const title = get(chat, 'messages.0.content.0.value');

      this.chatDate.set(date);

      this.history.update(history => {
        const existingChat = find(history, {id: this.chatId()});

        if (existingChat) {
          return history;
        }

        const newChat: HistoryItem = {
          title,
          id: this.chatId(),
          status: chat.status,
          assistantType: this.selectedAssistant(),
          createdAt: new Date().toISOString()
        };

        return [newChat, ...history];
      });
    });
  }

  private updateChatDateFromFirestore() {
    this.firestore
      .doc(this.chatRef)
      .get()
      .subscribe(doc => {
        const data = doc.data() as any;
        if (data?.createdAt) {
          const date = moment(data.createdAt).format('DD MMMM YYYY, HH:mm');
          this.chatDate.set(date);
        }
      });
  }

  private addChatToHistoryFromFirestore() {
    forkJoin({
      doc: this.firestore.doc(this.chatRef).get(),
      messages: this.firestore
        .collection(`${this.chatRef}/messages`, ref => ref.orderBy('createdAt').orderBy('__name__'))
        .get()
    }).subscribe(({doc, messages}) => {
      const chat = doc.data() as any;
      if (!chat) return;

      const date = moment(chat.createdAt).format('DD MMMM YYYY, HH:mm');
      const firstMessage = messages.docs[0]?.data() as Message;
      const title = firstMessage?.content?.[0]?.value ?? '';

      this.chatDate.set(date);

      this.history.update(history => {
        const existingChat = find(history, {id: this.chatId()});
        if (existingChat) return history;

        const newChat: HistoryItem = {
          title,
          id: this.chatId(),
          status: chat.status,
          assistantType: this.selectedAssistant(),
          createdAt: new Date().toISOString()
        };

        return [newChat, ...history];
      });
    });
  }
}
